import React from 'react';
import { Link } from 'react-router-dom';
import { getMainDomainUrl } from '../Utils/domainConfig';

interface SmartLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  target?: string;
  forceFull?: boolean;
}

const isGamepagePath = (p?: string) => {
  if (!p) return false;
  try {
    const parsed = new URL(p, window.location.origin);
    return /^\/gamepage(\/|$)/i.test(parsed.pathname);
  } catch {
    return /^\/gamepage(\/|$)/i.test(p);
  }
};

const extractGameSvcCd = (input?: string): string | null => {
  if (!input) return null;
  try {
    const u = new URL(input, window.location.origin);
    let found: string | null = null;
    u.searchParams.forEach((v, k) => {
      if (!found && k.toLowerCase() === 'gamesvccd') found = v || null;
    });
    return found;
  } catch {
    // fallback: parse querystring manually
    const idx = input.indexOf('?');
    if (idx === -1) return null;
    const qs = input.slice(idx + 1);
    const params = new URLSearchParams(qs);
    let found: string | null = null;
    params.forEach((v, k) => {
      if (!found && k.toLowerCase() === 'gamesvccd') found = v || null;
    });
    return found;
  }
};

export const SmartLink: React.FC<SmartLinkProps> = ({ to, children, className, style, onClick, target, forceFull }) => {
  // absolute external URL
  const isExternal = /^https?:\/\//i.test(to);

  if (isExternal) {
    return (
      <a href={to} className={className} style={style} onClick={onClick} target={target} rel={target === '_blank' ? 'noopener noreferrer' : undefined}>
        {children}
      </a>
    );
  }

  const currentPath = window.location.pathname;
  // normalize target path (keep query)
  let targetPath = to;
  try {
    targetPath = new URL(to, window.location.origin).pathname + new URL(to, window.location.origin).search;
  } catch {
    // leave as-is
  }

  const currentIsGame = isGamepagePath(currentPath);
  const targetIsGame = isGamepagePath(targetPath);

  // Force full navigation for any /gamepage target so server-side redirects run.
  const targetPathnameOnly = (() => {
    try {
      return new URL(to, window.location.origin).pathname;
    } catch {
      // if 'to' is a path already
      const m = to.match(/^[^?#]*/);
      return m ? m[0] : to;
    }
  })();
  const isTargetGamepage = /^\/gamepage(\/|$)/i.test(targetPathnameOnly);

  // Decide: use SPA Link when caller didn't force full nav and both current
  // and target are in the same context (both under /gamepage or both not).
  // Special case: if both are /gamepage but their `gameSvcCd` differs,
  // treat as different context and force a full href navigation so the
  // server-side mapping can move the host.
  let useLink = !forceFull && (currentIsGame === targetIsGame);

  if (currentIsGame && targetIsGame) {
    const currentCode = extractGameSvcCd(window.location.href);
    let targetHref = to;
    try {
      // resolve relative `to` against current origin
      targetHref = new URL(to, window.location.origin).href;
    } catch {}
    const targetCode = extractGameSvcCd(targetHref);
    // if codes exist and differ (case-insensitive) -> do NOT use Link
    if (currentCode || targetCode) {
      if (!currentCode || !targetCode || currentCode.toLowerCase() !== targetCode.toLowerCase()) {
        useLink = false;
      }
    }
  }

  if (useLink) {
    return (
      <Link to={to} className={className} style={style} onClick={onClick as any}>
        {children}
      </Link>
    );
  }

  // Otherwise produce an absolute href that points to the main domain so
  // server-side rewrites/redirects run as expected. If `to` is already
  // absolute (http/https), keep it.
  let href = to;
  if (!/^https?:\/\//i.test(to)) {
    // Ensure we pass path+search to getMainDomainUrl
    try {
      const parsed = new URL(to, window.location.origin);
      href = getMainDomainUrl(parsed.pathname + parsed.search);
    } catch {
      // fallback: treat `to` as path
      href = getMainDomainUrl(to);
    }
  }

  return (
    <a href={href} className={className} style={style} onClick={onClick} target={target} rel={target === '_blank' ? 'noopener noreferrer' : undefined}>
      {children}
    </a>
  );
};

export default SmartLink;

export {};

